<script setup lang="ts">
import ResponsiveMedia from '~/components/ResponsiveMedia.vue'

const containerElem = ref(null)
const {get_value: get_localized_value} = useLocalizedValue()

const swiper = useSwiper(containerElem)

const activeIndex = ref(0)

type CarouselItem = {
    media: string
    text?: string | LocalizedValue<string> | null
}

const { items } = defineProps({
    items: Array as PropType<CarouselItem[]>,
    properties: Object,
    content: Object
})

const getCaption = (text: CarouselItem['text']): string => {
    if (!text) return ''
    return get_localized_value<string>(text) ?? ''
}

watch(() => swiper.instance.value, () => {
    if (!swiper.instance?.value) return
    swiper.instance.value.on('slideChange', () => {
        activeIndex.value = swiper.instance.value.activeIndex
    })
})

</script>

<template>
    <div class="flex flex-col gap-4 items-center col-span-full">
        <ClientOnly>
            <swiper-container
                ref="containerElem"
                class="w-screen max-w-[120rem] !gap-4"
                :slides-per-view="1.3"
                centered-slides
                :space-between="0"
            >
                <swiper-slide v-for="(item, idx) in items" :key="idx">
                    <div class="slide-card">
                        <div class="slide-image">
                            <ResponsiveMedia
                                :media-id="item.media"
                                :alt="getCaption(item.text)"
                                sizes="(min-width: 120rem) 92rem, 77vw"
                                css-class="w-full h-full object-cover bg-[#f0f0f0] rounded-sm max-h-full"
                                :playing="idx === activeIndex"
                                muted
                                loop
                            />
                        </div>
                        <div v-if="getCaption(item.text)" class="text-caption">
                            <h3>{{ getCaption(item.text) }}</h3>
                        </div>
                    </div>
                </swiper-slide>
            </swiper-container>
        </ClientOnly>
        <div class="flex flex-row gap-2">
            <div v-for="(dot, index) in items" class="border w-2 h-2 rounded-full border-dark-blue bg-dark-blue/0 transition-colors" :class="{'bg-dark-blue/100': index === activeIndex}"></div>
        </div>
    </div>
</template>

<style lang="css" scoped>
swiper-slide {
    display: flex;
    justify-content: center;
    align-items: center;
    height: auto;
    overflow: visible;
    transition: transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94),
    filter 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    transform: scale(0.95);
    filter: opacity(70%);
}

.swiper-slide-active {
    transform: scale(1);
    filter: opacity(100%);
}

.slide-card {
    min-height: 300px;
    display: flex;
    flex-direction: column;
    width: 100%;
    align-self: self-start;
}

.slide-image {
    width: 100%;
    aspect-ratio: 16/9;
    overflow: hidden;
    border-radius: 0.25rem;
}

.slide-image :deep(video),
.slide-image :deep(img) {
    width: 100%;
    height: 100%;
    object-fit: cover;
    background: #f0f0f0;
    border-radius: 0.25rem;
    max-height: 100%;
}

.text-caption {
    padding: 0.75rem 0.25rem;
    flex-shrink: 0;
}
</style>
